import React, { useState, useRef } from 'react';
import { 
  PaperClipIcon, 
  CloudArrowUpIcon,
  XMarkIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import { uploadAttachment, formatFileSize } from '../../lib/attachments';
import { toast } from 'react-toastify';

interface FileUploadProps {
  ideaId?: string;
  taskId?: string;
  onUploadComplete: () => void;
  disabled?: boolean;
  className?: string;
}

interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({ 
  ideaId, 
  taskId, 
  onUploadComplete, 
  disabled = false,
  className = '' 
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;

    const fileArray = Array.from(files);
    
    // Initialize upload progress for all files
    const initialProgress: UploadProgress[] = fileArray.map(file => ({
      fileName: file.name,
      progress: 0,
      status: 'uploading'
    }));
    
    setUploads(prev => [...prev, ...initialProgress]);

    // Upload files one by one
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      
      try {
        // Update progress to show starting
        setUploads(prev => prev.map(upload => 
          upload.fileName === file.name && upload.status === 'uploading'
            ? { ...upload, progress: 10 }
            : upload
        ));

        // Simulate upload progress (since Supabase doesn't provide real-time progress)
        const progressInterval = setInterval(() => {
          setUploads(prev => prev.map(upload => 
            upload.fileName === file.name && upload.status === 'uploading'
              ? { ...upload, progress: Math.min(upload.progress + 15, 90) }
              : upload
          ));
        }, 200);

        // Perform the actual upload
        await uploadAttachment({ file, ideaId, taskId });

        // Clear progress interval and mark as complete
        clearInterval(progressInterval);
        setUploads(prev => prev.map(upload => 
          upload.fileName === file.name && upload.status === 'uploading'
            ? { ...upload, progress: 100, status: 'success' }
            : upload
        ));

        toast.success(`✅ ${file.name} uploaded successfully!`);
        
      } catch (error: any) {
        // Mark as error
        setUploads(prev => prev.map(upload => 
          upload.fileName === file.name && upload.status === 'uploading'
            ? { ...upload, status: 'error', error: error.message }
            : upload
        ));

        toast.error(`❌ Failed to upload ${file.name}: ${error.message}`);
        console.error(`Upload failed for ${file.name}:`, error);
      }
    }

    // Clear completed uploads after a delay
    setTimeout(() => {
      setUploads(prev => prev.filter(upload => upload.status === 'uploading'));
      onUploadComplete();
    }, 3000);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    handleFiles(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const clearUpload = (fileName: string) => {
    setUploads(prev => prev.filter(upload => upload.fileName !== fileName));
  };

  const hasActiveUploads = uploads.some(upload => upload.status === 'uploading');

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200
          ${dragActive 
            ? 'border-deep-teal bg-deep-teal bg-opacity-10' 
            : disabled
              ? 'border-gray-200 bg-gray-50'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }
          ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
          accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
          disabled={disabled}
        />

        <div className="space-y-2">
          {dragActive ? (
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-deep-teal" />
          ) : (
            <PaperClipIcon className={`mx-auto h-12 w-12 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />
          )}
          
          <div>
            <p className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
              {dragActive ? (
                <span className="text-deep-teal font-medium">Drop files here to upload</span>
              ) : (
                <>
                  <span className={`font-medium ${disabled ? 'text-gray-400' : 'text-electric-lime'}`}>
                    Click to browse
                  </span>
                  {!disabled && ' or drag and drop files here'}
                </>
              )}
            </p>
            
            {!disabled && (
              <p className="text-xs text-gray-500 mt-1">
                Images, PDFs, documents, spreadsheets up to 10MB each
              </p>
            )}
          </div>
        </div>

        {disabled && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-50 rounded-lg flex items-center justify-center">
            <span className="text-sm text-gray-500">Upload disabled</span>
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {uploads.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            {hasActiveUploads ? 'Uploading files...' : 'Upload results'}
          </h4>
          
          {uploads.map((upload, index) => (
            <div
              key={`${upload.fileName}-${index}`}
              className="bg-white border border-gray-200 rounded-lg p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  {upload.status === 'success' ? (
                    <div className="w-4 h-4 rounded-full bg-green-500 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  ) : upload.status === 'error' ? (
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                  ) : (
                    <div className="w-4 h-4 border-2 border-deep-teal border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  )}
                  
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {upload.fileName}
                  </span>
                  
                  {upload.status !== 'uploading' && (
                    <button
                      onClick={() => clearUpload(upload.fileName)}
                      className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {upload.status === 'uploading' && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-deep-teal h-2 rounded-full transition-all duration-300"
                    style={{ width: `${upload.progress}%` }}
                  />
                </div>
              )}

              {upload.status === 'error' && upload.error && (
                <p className="text-xs text-red-600 mt-1">{upload.error}</p>
              )}

              {upload.status === 'success' && (
                <p className="text-xs text-green-600 mt-1">Upload completed successfully!</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* File Requirements */}
      {!disabled && (
        <div className="text-xs text-gray-500 space-y-1">
          <p><strong>Supported formats:</strong></p>
          <ul className="list-disc list-inside space-y-0.5 ml-2">
            <li>Images: JPEG, PNG, GIF, WebP, SVG</li>
            <li>Documents: PDF, Word, Excel, PowerPoint</li>
            <li>Text: Plain text, CSV</li>
            <li>Archives: ZIP files</li>
          </ul>
          <p><strong>Maximum file size:</strong> 10MB per file</p>
        </div>
      )}
    </div>
  );
};

export default FileUpload; 